import { Injectable, OnDestroy } from '@angular/core';
import { NgbModalRef, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FeatureModalComponent } from './feature-modal.component';
import { OrganizationService } from '../organizations/organization.service';
import { takeUntil, filter } from 'rxjs/operators';
import { Organization } from '../shared/sdk';
import { Subject } from 'rxjs';
import { StorageService } from '../shared/services/storage.service';

export interface FeatureModalOptions {
  onlyOnce?: boolean;
  showCTA?: boolean;
  showClose?: boolean;
  clearSidebar?: boolean;
  appendTo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureModalService implements OnDestroy {
  activeOrg: Organization;
  currentFeatureModalType: string;
  currentFeatureModalRef: NgbModalRef;
  private cookiePrefix = 'Apollo:fms:';
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private modalService: NgbModal,
    private storageService: StorageService,
    private orgService: OrganizationService,
  ) {
    this.orgService.active
      .pipe(
        filter((org) => !!org),
        takeUntil(this.onDestroy$)
      )
      .subscribe((org) => (this.activeOrg = org));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  open(
    type: string,
    {
      onlyOnce = false,
      showCTA = true,
      showClose = true,
      clearSidebar = true,
      appendTo = '.main-content',
    }: FeatureModalOptions = {},
  ) {
    let backdropClass = '';

    if (clearSidebar) backdropClass += ' clear-sidebar';

    if (onlyOnce) {
      const seenFeatureModal = this.hasSeen(type);

      if (seenFeatureModal) {
        return;
      }
    }

    this.markSeen(type);

    if (this.currentFeatureModalType) {
      if (this.currentFeatureModalType !== type) {
        this.currentFeatureModalRef.dismiss();
      } else {
        return;
      }
    }

    this.currentFeatureModalType = type;

    this.currentFeatureModalRef = this.modalService.open(
      FeatureModalComponent,
      {
        container: appendTo,
        keyboard: false,
        backdrop: 'static',
        backdropClass,
        centered: true,
      },
    );

    this.currentFeatureModalRef.componentInstance.type = type;
    this.currentFeatureModalRef.componentInstance.orgId = this.activeOrg.id;
    this.currentFeatureModalRef.componentInstance.showCTA = showCTA;
    this.currentFeatureModalRef.componentInstance.showClose = showClose;

    const result = this.currentFeatureModalRef.result;

    result
      .then(() => (this.currentFeatureModalType = null))
      .catch(() => (this.currentFeatureModalType = null));

    return result;
  }

  close() {
    if (this.currentFeatureModalType) {
      this.currentFeatureModalRef.dismiss();
    }
  }

  hasSeen(type: string) {
    const cookieName = `${this.cookiePrefix}${this.activeOrg.id}:${type}`;
    return Boolean(this.storageService.getItem(cookieName));
  }

  markSeen(type: string) {
    const cookieName = `${this.cookiePrefix}${this.activeOrg.id}:${type}`;
    this.storageService.setItem(cookieName, 'true');
  }
}
